import type { Operation, ServerError } from "@apollo/client"
import { fromPromise } from "@apollo/client"
import { captureException, withScope } from "@sentry/react"
import { type ErrorResponse, onError } from "@apollo/client/link/error"
import { onTokenExpired } from "@/v1-console/auth/auth.instance"
import { getIsAuthServerError } from "@/v1-console/apollo/apollo.utils"
import config from "@/v1-console/config"

// Raised by the shared graphql-ws transport rather than by any one operation.
const SOCKET_ERROR_MESSAGE = /^socket closed/i

function reportNetworkError(networkError: Error, operation: Operation) {
  withScope((scope) => {
    const { operationName } = operation
    scope.setTag("graphql.operationName", operationName)
    scope.setTag("type", "network")

    // Captured without a scope, a socket error inherits whichever
    // `graphql.request` transaction apollo.trace.ts last pinned to the shared
    // scope - a query, since the trace link only wraps the HTTP path. That
    // smeared one transport fault across every operation name in the app.
    // Fingerprint it on the transport so it groups as a single issue.
    scope.setFingerprint(SOCKET_ERROR_MESSAGE.test(networkError.message)
      ? [ "graphql", "network", "socket" ]
      : [ "graphql", "network", operationName ]
    )

    captureException(networkError)
  })
}

function report(props: ErrorResponse) {
  const { networkError, graphQLErrors, operation } = props

  if(networkError && config.isInstrumentationEnabled) {
    reportNetworkError(networkError, operation)
  }

  if(!graphQLErrors) return

  for(const _error of graphQLErrors) {
    const error: any = _error
    withScope((scope) => {
      const { operationName, variables, extensions } = operation

      const ctx: Record<string, unknown> = { operationName }
      if(variables) ctx.variables = JSON.stringify(variables)
      if(extensions) ctx.extensions = JSON.stringify(extensions)
      if(error.path) ctx.path = error.path.join(" > ")
      if(error.source) ctx.source = JSON.stringify(error.source)
      if(error.extensions?.code) ctx.errorCode = error.extensions.code
      if(error.extensions?.stacktrace) ctx.stacktrace = error.extensions.stacktrace

      const fingerprint = [ "graphql", operationName ]

      if(error.originalError) {
        fingerprint.push(error.originalError.constructor.name)
      }

      scope.setContext("GraphQL", ctx)
      scope.setTag("graphql.operationName", operationName)
      scope.setTransactionName(operationName)
      scope.setFingerprint(fingerprint)

      captureException(error.originalError
        ? error.originalError
        : new Error(error.message)
      , scope)
    })
  }
}

export const errorLink = onError((error) => {
  const { networkError, graphQLErrors, operation, forward } = error

  if(networkError && getIsAuthServerError(networkError as ServerError)) {
    return fromPromise(onTokenExpired())
      .flatMap(() => forward(operation))
  }

  if(config.isInstrumentationEnabled) {
    report(error)
  } else {
    // eslint-disable-next-line no-console
    if(networkError) console.error("networkError", networkError)
    // eslint-disable-next-line no-console
    if(graphQLErrors) console.error(graphQLErrors)
  }
})
